import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { TID } from '@/constants/testIds/voice';
import { Plus, X, PencilSimple, Trash, Key } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const blank = {
    extension: '',
    display_name: '',
    sip_username: '',
    password: '',
    assigned_user_email: '',
    outbound_allowed: true,
    outbound_caller_id: '',
    is_active: true,
};

const Extensions = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showPwd, setShowPwd] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        setLoading(true);
        const r = await api.get('/extensions');
        setItems(r.data);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...editing };
            if (!payload.password) delete payload.password;
            if (!payload.assigned_user_email) payload.assigned_user_email = null;
            if (!payload.outbound_caller_id) payload.outbound_caller_id = null;
            let res;
            if (editing.id) {
                res = await api.patch(`/extensions/${editing.id}`, payload);
            } else {
                res = await api.post('/extensions', payload);
            }
            setEditing(null);
            if (res.data.initial_password) {
                setShowPwd({ extension: res.data.extension, password: res.data.initial_password });
            }
            load();
            toast({ title: 'Uloženo', description: `Klapka ${res.data.extension}` });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };

    const del = async (id) => {
        if (!window.confirm('Smazat klapku?')) return;
        await api.delete(`/extensions/${id}`);
        load();
    };

    const regen = async (id) => {
        const r = await api.post(`/extensions/${id}/regenerate-password`);
        setShowPwd({ extension: id, password: r.data.new_password });
    };

    return (
        <>
            <PageHeader
                eyebrow="SIP"
                title="SIP Klapky / Extensions"
                description="Interní SIP účty (101, 102, 103…) pro IP telefony, softphone aplikace a směrování. Hesla jsou hashovaná, nikdy se neukládají v plaintextu."
                actions={
                    <button
                        data-testid={TID.extCreateBtn}
                        onClick={() => setEditing({ ...blank, extension: nextExt(items), sip_username: nextExt(items) })}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                        style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}
                    >
                        <Plus size={14} weight="bold" />
                        Nová klapka
                    </button>
                }
            />

            <Card testId={TID.extTable}>
                {loading ? <div>Načítání…</div> : items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Zatím žádné klapky.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]"
                                style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Klapka</th>
                                <th className="text-left">Jméno</th>
                                <th className="text-left">SIP user</th>
                                <th className="text-left">Uživatel</th>
                                <th className="text-center">Registrace</th>
                                <th className="text-center">Outbound</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => (
                                <tr key={e.id} className="border-t"
                                    style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono font-bold">{e.extension}</td>
                                    <td>{e.display_name}</td>
                                    <td className="font-mono text-xs">{e.sip_username}</td>
                                    <td className="text-xs">{e.assigned_user_email || '—'}</td>
                                    <td className="text-center">
                                        <StatusPill
                                            status={e.registration_status === 'online' ? 'success' : 'neutral'}
                                            label={e.registration_status}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <StatusPill
                                            status={e.outbound_allowed ? 'success' : 'error'}
                                            label={e.outbound_allowed ? 'povoleno' : 'blokováno'}
                                        />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => regen(e.id)}
                                                title="Nové heslo"
                                                className="p-1 hover:bg-[var(--bg-surface-hover)]"
                                                data-testid={`ext-regen-${e.id}`}>
                                            <Key size={14} />
                                        </button>
                                        <button onClick={() => setEditing({ ...e })}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1"
                                                data-testid={`ext-edit-${e.id}`}>
                                            <PencilSimple size={14} />
                                        </button>
                                        <button onClick={() => del(e.id)}
                                                className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1"
                                                data-testid={`ext-delete-${e.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title={editing.id ? 'Editace klapky' : 'Nová SIP klapka'} onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Klapka" value={editing.extension}
                               onChange={(v) => setEditing({ ...editing, extension: v })} required mono />
                        <Field label="Display name" value={editing.display_name}
                               onChange={(v) => setEditing({ ...editing, display_name: v })} required />
                        <Field label="SIP username" value={editing.sip_username}
                               onChange={(v) => setEditing({ ...editing, sip_username: v })} required mono />
                        <Field label={editing.id ? 'Nové heslo (nebo nechat prázdné)' : 'Heslo (necháte-li, vygeneruje se)'}
                               value={editing.password || ''}
                               onChange={(v) => setEditing({ ...editing, password: v })} type="password" mono />
                        <Field label="Přiřazený CMS user (email)" value={editing.assigned_user_email || ''}
                               onChange={(v) => setEditing({ ...editing, assigned_user_email: v })} />
                        <Field label="Outbound Caller ID" value={editing.outbound_caller_id || ''}
                               onChange={(v) => setEditing({ ...editing, outbound_caller_id: v })} mono />
                        <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!editing.outbound_allowed}
                                   onChange={(e) => setEditing({ ...editing, outbound_allowed: e.target.checked })} />
                            Povolit odchozí hovory
                        </label>
                        <label className="col-span-2 inline-flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={!!editing.is_active}
                                   onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                            Aktivní
                        </label>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)}
                                    className="px-3 py-2 text-xs font-bold uppercase border"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid={TID.extSaveBtn}
                                    className="px-3 py-2 text-xs font-bold uppercase"
                                    style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                                Uložit
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {showPwd && (
                <Modal title="SIP heslo (zobrazí se pouze jednou)" onClose={() => setShowPwd(null)}>
                    <p className="text-sm mb-3">
                        Toto heslo si nyní zkopírujte – v systému ho ukládáme jen jako hash a později ho už neuvidíte.
                    </p>
                    <code className="block px-3 py-2 font-mono text-sm break-all"
                          style={{ background: '#06080A', borderRadius: 2 }}>
                        {showPwd.password}
                    </code>
                    <button onClick={() => navigator.clipboard.writeText(showPwd.password)}
                            className="mt-3 px-3 py-2 text-xs font-bold uppercase border"
                            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                        Zkopírovat
                    </button>
                </Modal>
            )}
        </>
    );
};

const nextExt = (items) => {
    const nums = items.map((i) => parseInt(i.extension, 10)).filter((n) => !Number.isNaN(n));
    return String(Math.max(100, ...nums, 100) + 1);
};

export const Modal = ({ title, onClose, children, wide }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(11,14,20,0.85)' }}>
        <div className={`w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} border p-6 max-h-[90vh] overflow-y-auto`}
             style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)', borderRadius: 2 }}>
            <div className="flex items-start justify-between mb-4">
                <h2 className="font-heading text-xl font-bold">{title}</h2>
                <button onClick={onClose} type="button"><X size={20} /></button>
            </div>
            {children}
        </div>
    </div>
);

export const Field = ({ label, value, onChange, type = 'text', required, mono }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.1em] font-bold"
               style={{ color: 'var(--text-secondary)' }}>{label}</label>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            className={`w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm ${mono ? 'font-mono' : ''}`}
            style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}
        />
    </div>
);

export default Extensions;
